import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import FamilyMemberSelector from "../../components/FamilyMemberSelector";
import { AuthContext } from "../../components/AuthContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../config";
import { getAuthToken } from "../../utils/authStorage";
import { formatUtcDateTimeInSriLanka } from "../../utils/dateTime";

const MemberPayment = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const navigate = useNavigate();
  const { role, familyMembers, activeMember, selectedMemberId, selectFamilyMember } =
    useContext(AuthContext);
  const locationMember = location.state?.member || null;
  const [availableMembers, setAvailableMembers] = useState(
    familyMembers.length > 0 ? familyMembers : locationMember ? [locationMember] : []
  );
  const [currentMemberId, setCurrentMemberId] = useState(
    selectedMemberId || locationMember?.member_id || activeMember?.member_id || null
  );
  const [memberProfile, setMemberProfile] = useState(locationMember || activeMember || null);

  const [invoice, setInvoice] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [lastPaymentData, setLastPaymentData] = useState(null);

  const isAdmin = role === "global_admin" || role === "branch_admin";
  const isMember = role === "member";

  const getAuthorizedRequestConfig = () => {
    const token = getAuthToken();
    return token
      ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
      : {};
  };

  useEffect(() => {
    if (familyMembers.length > 0) {
      setAvailableMembers(familyMembers);
    }
  }, [familyMembers]);

  useEffect(() => {
    if (!currentMemberId) {
      const fallbackId =
        selectedMemberId ||
        activeMember?.member_id ||
        locationMember?.member_id ||
        availableMembers[0]?.member_id ||
        null;

      if (fallbackId) {
        setCurrentMemberId(fallbackId);
      }
    }
  }, [activeMember, availableMembers, currentMemberId, locationMember, selectedMemberId]);

  const selectedMember = useMemo(
    () =>
      availableMembers.find(
        (member) => String(member.member_id) === String(currentMemberId)
      ) || memberProfile || activeMember || locationMember || null,
    [activeMember, availableMembers, currentMemberId, locationMember, memberProfile]
  );

  const effectiveMember = memberProfile || selectedMember;

  // Generate and open receipt in new tab
  const openPrintReceipt = (paymentData) => {
    const receiptHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Payment Receipt</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Courier New', monospace;
      width: 80mm;
      max-width: 80mm;
      margin: 0 auto;
      padding: 5mm;
      font-size: 12px;
      line-height: 1.4;
      background: #fff;
      color: #000;
    }
    .receipt {
      width: 100%;
    }
    .header {
      text-align: center;
      border-bottom: 1px dashed #000;
      padding-bottom: 8px;
      margin-bottom: 8px;
    }
    .header h1 {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 4px;
    }
    .header p {
      font-size: 10px;
    }
    .section {
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px dashed #000;
    }
    .section:last-child {
      border-bottom: none;
    }
    .row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 2px;
    }
    .row .label {
      font-weight: normal;
    }
    .row .value {
      font-weight: bold;
      text-align: right;
    }
    .title {
      font-weight: bold;
      font-size: 13px;
      margin-bottom: 4px;
      text-transform: uppercase;
    }
    .divider {
      border-top: 1px dashed #000;
      margin: 8px 0;
    }
    .total-row {
      font-size: 14px;
      font-weight: bold;
      margin-top: 4px;
    }
    .total-row .value {
      font-size: 16px;
    }
    .footer {
      text-align: center;
      margin-top: 12px;
      font-size: 10px;
    }
    .footer p {
      margin-bottom: 4px;
    }
    .center {
      text-align: center;
    }
    .bold {
      font-weight: bold;
    }
    @media print {
      body {
        width: 80mm;
        margin: 0;
        padding: 2mm;
      }
      @page {
        size: 80mm auto;
        margin: 0;
      }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <h1>TEBA</h1>
      <p>Payment Receipt</p>
      <p>${formatUtcDateTimeInSriLanka(new Date())}</p>
    </div>

    <div class="section">
      <div class="row">
        <span class="label">Receipt No:</span>
        <span class="value">${paymentData.receiptNo || 'N/A'}</span>
      </div>
      <div class="row">
        <span class="label">Member ID:</span>
        <span class="value">${paymentData.membershipId}</span>
      </div>
      <div class="row">
        <span class="label">Name:</span>
        <span class="value">${paymentData.memberName}</span>
      </div>
    </div>

    <div class="section">
      <div class="title">Payment Details</div>
      ${paymentData.arrearsMonths && paymentData.arrearsMonths.length > 0 ? `
        <div style="margin-bottom: 6px;">
          <div class="row" style="margin-bottom: 4px;">
            <span class="label bold">Arrears (incl. ${paymentData.surchargePercent}% surcharge):</span>
            <span class="value"></span>
          </div>
          ${paymentData.arrearsMonths.map(m => `
            <div class="row">
              <span class="label">  ${m.monthName}</span>
              <span class="value">LKR ${m.amount.toFixed(2)}</span>
            </div>
          `).join('')}
          <div class="row" style="margin-top: 4px;">
            <span class="label bold">  Arrears Total:</span>
            <span class="value bold">LKR ${paymentData.arrearsTotal.toFixed(2)}</span>
          </div>
        </div>
      ` : ''}
      <div class="row">
        <span class="label">${paymentData.currentMonth} Fee:</span>
        <span class="value">LKR ${paymentData.currentMonthAmount.toFixed(2)}</span>
      </div>
      ${paymentData.currentMonthSurcharge > 0 ? `
        <div class="row">
          <span class="label">Current Month Surcharge (${paymentData.surchargePercent}%):</span>
          <span class="value">LKR ${paymentData.currentMonthSurcharge.toFixed(2)}</span>
        </div>
      ` : ''}
    </div>

    <div class="section">
      <div class="row total-row">
        <span class="label">TOTAL PAID:</span>
        <span class="value">LKR ${paymentData.grandTotal.toFixed(2)}</span>
      </div>
    </div>

    <div class="footer">
      <p>--------------------------------</p>
      <p>Thank you for your payment!</p>
      <p>This is a computer generated receipt</p>
      <p>--------------------------------</p>
    </div>
  </div>
  <script>
    window.onload = function() {
      window.print();
    }
  </script>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  useEffect(() => {
    if (!selectedMember?.membership_id) {
      setLoading(false);
      return;
    }

    fetchInvoiceAndPayments(selectedMember.membership_id);
  }, [selectedMember?.membership_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMemberSelection = (memberId) => {
    setCurrentMemberId(memberId);
    setLastPaymentData(null);
    if (isMember) {
      selectFamilyMember(memberId);
    }
  };

  const fetchInvoiceAndPayments = async (membershipId) => {
    const requestConfig = getAuthorizedRequestConfig();

    try {
      setLoading(true);
      const [profileResult, invoiceResult, paymentResult] = await Promise.allSettled([
        axios.get(`${config.apiBaseUrl}/member_profile/${membershipId}`, requestConfig),
        axios.post(
          `${config.apiBaseUrl}/calculate_monthly_invoice`,
          {
            membership_id: membershipId,
          },
          requestConfig
        ),
        axios.post(
          `${config.apiBaseUrl}/get_payment_history`,
          {
            membership_id: membershipId,
          },
          requestConfig
        ),
      ]);

      if (profileResult.status === "fulfilled") {
        setMemberProfile(profileResult.value.data.member || null);
        if (Array.isArray(profileResult.value.data.family_members)) {
          setAvailableMembers(profileResult.value.data.family_members);
        }
      } else {
        console.error("Failed to load member profile:", profileResult.reason);
        toast.error(profileResult.reason?.response?.data?.error || "Failed to load member profile");
      }

      if (invoiceResult.status === "fulfilled") {
        const invoiceData = invoiceResult.value.data.invoice;
        setInvoice(invoiceData);
        if (invoiceData.calculation_type === "per_day") {
          setCustomAmount(invoiceData.total_amount > 0 ? invoiceData.total_amount.toFixed(2) : "");
        } else {
          setCustomAmount(invoiceData.total_amount?.toFixed(2) || "0");
        }
      } else {
        console.error("Failed to load invoice:", invoiceResult.reason);
        setInvoice(null);
        setCustomAmount("");
        toast.error(invoiceResult.reason?.response?.data?.error || "Failed to load invoice details");
      }

      if (paymentResult.status === "fulfilled") {
        setPaymentHistory(paymentResult.value.data.payments || []);
      } else {
        console.error("Failed to load payment history:", paymentResult.reason);
        setPaymentHistory([]);
        toast.error(paymentResult.reason?.response?.data?.error || "Failed to load payment history");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const calculateGrandTotal = () => {
    return parseFloat(customAmount) || 0;
  };

  const needsPayment = () => {
    if (invoice?.calculation_type === "per_day") {
      return true;
    }
    return invoice?.is_expired === true;
  };

  const handleRecordPayment = async () => {
    if (!effectiveMember?.membership_id) {
      toast.error("Member is required to record a payment");
      return;
    }

    try {
      setConfirmDialogOpen(false);
      setProcessingPayment(true);
      const totalAmount = calculateGrandTotal();

      const response = await axios.post(`${config.apiBaseUrl}/record_payment`, {
        membership_id: effectiveMember.membership_id,
        amount: totalAmount,
      }, getAuthorizedRequestConfig());

      toast.success("Payment recorded successfully!");

      const arrearsMonths = invoice?.unpaid_months?.map((month) => ({
        monthName: month.month_name,
        amount: month.total || 0,
      })).filter(m => m.amount > 0) || [];

      const receiptData = {
        receiptNo: `RCP-${Date.now()}`,
        membershipId: effectiveMember.membership_id,
        memberName: `${effectiveMember.first_name} ${effectiveMember.last_name}`,
        subscriptionType: invoice?.subscription_type,
        arrearsMonths: arrearsMonths,
        arrearsTotal: invoice?.arrears_total || 0,
        currentMonth: `${invoice?.month}/${invoice?.year}`,
        currentMonthAmount: invoice?.monthly_fee || 0,
        surchargePercent: invoice?.surcharge_percent || 0,
        currentMonthSurcharge: invoice?.current_month_surcharge || 0,
        grandTotal: totalAmount,
        expiryDate: response.data.expiry_date,
      };
      setLastPaymentData(receiptData);

      openPrintReceipt(receiptData);
      fetchInvoiceAndPayments(effectiveMember.membership_id);
    } catch (error) {
      console.error("Failed to record payment:", error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Failed to record payment");
      }
    } finally {
      setProcessingPayment(false);
    }
  };

  const columns = [
    {
      field: "payment_date",
      headerName: "Payment Date",
      flex: 1,
    },
    {
      field: "expiry_date",
      headerName: "Expiry Date",
      flex: 1,
      renderCell: ({ row }) => row.expiry_date || "N/A (Per Day)",
    },
    {
      field: "subscription_name",
      headerName: "Subscription",
      flex: 1,
    },
    {
      field: "amount",
      headerName: "Amount (LKR)",
      flex: 1,
      valueFormatter: (value) => {
        return value ? `LKR ${parseFloat(value).toFixed(2)}` : 'N/A';
      },
    },
  ];

  if (loading) {
    return (
      <Box m={{ xs: "12px", sm: "16px", md: "20px" }} display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!effectiveMember) {
    return (
      <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
        <ToastContainer />
        <Header title="Member Payment" subtitle="Invoice and Payment History" />
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          {isAdmin
            ? "Open a member profile or member list entry first to view payments."
            : "No family member is available for this session."}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(isMember ? "/view_member" : "/member_list")} size={isMobile ? "small" : "medium"}>
          {isMember ? "Go to Family Profile" : "Go to Member List"}
        </Button>
      </Box>
    );
  }

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <ToastContainer />
      <Header title="Member Payment" subtitle="Invoice and Payment History" />

      <FamilyMemberSelector
        members={availableMembers}
        selectedMemberId={currentMemberId || effectiveMember.member_id}
        onChange={handleMemberSelection}
        label="Payment Member"
      />

      <Box display="flex" justifyContent="flex-start" alignItems="center" gap={1} flexWrap="wrap" mb={{ xs: 2, sm: 3 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(isMember ? "/view_member" : -1)}
          size={isMobile ? "small" : "medium"}
          sx={{
            borderColor: colors.grey[100],
            color: colors.grey[100],
            "&:hover": {
              borderColor: colors.greenAccent[400],
              color: colors.greenAccent[400],
            },
          }}
        >
          Back to Members
        </Button>
        {lastPaymentData && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={() => openPrintReceipt(lastPaymentData)}
            size={isMobile ? "small" : "medium"}
          >
            Reprint Last Receipt
          </Button>
        )}
      </Box>

      {/* Member Info */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: { xs: 2, sm: 3 }, backgroundColor: colors.primary[400] }}>
        <Typography variant="h4" fontWeight="600" mb={2} sx={{ fontSize: { xs: "1.3rem", sm: "1.6rem", md: "2rem" } }}>
          {effectiveMember.first_name} {effectiveMember.last_name}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1">
              <strong>Membership ID:</strong> {effectiveMember.membership_id}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1">
              <strong>Branch:</strong> {effectiveMember.branch_name || "N/A"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Unpaid Previous Months (Arrears) */}
      {invoice && invoice.unpaid_months && invoice.unpaid_months.length > 0 && (
        <Card sx={{ mb: { xs: 2, sm: 3 }, backgroundColor: colors.primary[400], border: `2px solid ${colors.redAccent[500]}` }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} flexDirection={{ xs: "column", sm: "row" }} gap={1} mb={2}>
              <Typography variant="h4" fontWeight="600" color={colors.redAccent[400]} sx={{ fontSize: { xs: "1.1rem", sm: "1.4rem", md: "2rem" } }}>
                ⚠️ Expired Months (Arrears)
              </Typography>
              <Typography variant="h5" fontWeight="700" color={colors.redAccent[400]} sx={{ fontSize: { xs: "1rem", sm: "1.2rem", md: "1.5rem" } }}>
                Total: LKR {invoice.arrears_total?.toFixed(2)}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Your subscription expired. You have <strong>{invoice.unpaid_months.length} unpaid month(s)</strong> since expiry.
              </Typography>
            </Alert>

            {invoice.unpaid_months.map((unpaidMonth, index) => (
              <Paper
                key={index}
                elevation={2}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: colors.primary[500],
                  borderLeft: `4px solid ${colors.redAccent[500]}`
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="h6" fontWeight="600" color={colors.redAccent[300]}>
                      {unpaidMonth.month_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="body2" color={colors.grey[300]}>Subscription Fee</Typography>
                    <Typography variant="body1">LKR {unpaidMonth.subscription_fee?.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="body2" color={colors.grey[300]}>Late Surcharge ({invoice.surcharge_percent}%)</Typography>
                    <Typography variant="body1" color={colors.redAccent[300]}>LKR {unpaidMonth.surcharge?.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color={colors.grey[300]}>Amount Due</Typography>
                    <Typography variant="h6" color={colors.redAccent[400]}>
                      LKR {unpaidMonth.total?.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Current Month Invoice Card */}
      {invoice && (
        <Card sx={{ mb: { xs: 2, sm: 3 }, backgroundColor: colors.primary[400] }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} flexDirection={{ xs: "column", sm: "row" }} gap={1} mb={3}>
              <Typography variant="h4" fontWeight="600" color={colors.greenAccent[400]} sx={{ fontSize: { xs: "1.1rem", sm: "1.4rem", md: "2rem" } }}>
                Subscription Status ({invoice.month}/{invoice.year})
              </Typography>
              {invoice.no_payment_due && (
                <Alert severity="success" sx={{ py: 0 }}>
                  <Typography variant="body2"><strong>No payment due</strong></Typography>
                </Alert>
              )}
              {!invoice.no_payment_due && !invoice.is_expired && invoice.calculation_type !== "per_day" && (
                <Alert severity="success" sx={{ py: 0 }}>
                  <Typography variant="body2"><strong>ACTIVE</strong></Typography>
                </Alert>
              )}
              {invoice.is_expired && invoice.calculation_type !== "per_day" && (
                <Alert severity="error" sx={{ py: 0 }}>
                  <Typography variant="body2"><strong>EXPIRED</strong></Typography>
                </Alert>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <Typography variant="body2" color={colors.grey[100]}>
                    Subscription Name
                  </Typography>
                  <Typography variant="h6">
                    {invoice.subscription_name}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color={colors.grey[100]}>
                    Subscription Type
                  </Typography>
                  <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                    {invoice.subscription_type || 'N/A'}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color={colors.grey[100]}>
                    Subscription Fee
                  </Typography>
                  <Typography variant="h6">
                    LKR {invoice.subscription_fee?.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                {invoice.calculation_type === "per_day" ? (
                  <Box mb={2}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Per-Day Payment:</strong> Pay as you go. No expiry date.
                      </Typography>
                    </Alert>
                    <Box mt={2}>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Attendance Count (This Month)
                      </Typography>
                      <Typography variant="h6">
                        {invoice.attendance_count} days
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Box mb={2}>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Latest Expiry Date
                      </Typography>
                      <Typography variant="h6" color={invoice.is_expired ? colors.redAccent[400] : colors.greenAccent[400]}>
                        {invoice.latest_expiry_date || "No payment yet"}
                      </Typography>
                    </Box>

                    {invoice.is_expired && (
                      <Box mb={2}>
                        <Alert severity="warning">
                          <Typography variant="body2">
                            <strong>Subscription Expired!</strong> Please renew to continue.
                          </Typography>
                        </Alert>
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Summary Section */}
            {!invoice.no_payment_due && (invoice.is_expired || invoice.calculation_type === "per_day") && (
              <Box mb={3} p={2} sx={{ backgroundColor: colors.primary[500], borderRadius: 1 }}>
                <Typography variant="h5" fontWeight="600" mb={2}>Payment Summary</Typography>
                <Grid container spacing={2}>
                  {invoice.unpaid_months?.length > 0 && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color={colors.grey[300]}>
                        Arrears ({invoice.unpaid_months?.length} months, incl. {invoice.surcharge_percent}% surcharge)
                      </Typography>
                      <Typography variant="h6" color={colors.redAccent[400]}>
                        LKR {invoice.arrears_total?.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="body2" color={colors.grey[300]}>Current Month Fee</Typography>
                    <Typography variant="h6">LKR {invoice.monthly_fee?.toFixed(2)}</Typography>
                  </Grid>
                  {invoice.current_month_surcharge > 0 && (
                    <Grid item xs={6} sm={6} md={3}>
                      <Typography variant="body2" color={colors.redAccent[300]}>
                        Current Month Surcharge ({invoice.surcharge_percent}%)
                      </Typography>
                      <Typography variant="h6" color={colors.redAccent[400]}>
                        LKR {invoice.current_month_surcharge?.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color={colors.grey[300]}>Total Amount Due</Typography>
                    <Typography variant="h4" fontWeight="700" color={colors.redAccent[400]}>
                      LKR {invoice.total_amount?.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
                {invoice.arrears_surcharge_total > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Arrears Surcharge:</strong> {invoice.surcharge_percent}% late payment surcharge is included for each arrears month (Total: LKR {invoice.arrears_surcharge_total?.toFixed(2)}).
                    </Typography>
                  </Alert>
                )}
                {invoice.is_late_payment && invoice.current_month_surcharge > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Late Payment:</strong> A {invoice.surcharge_percent}% surcharge has been applied to current month as payment is after the {invoice.surcharge_day}th.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems={{ xs: "flex-start", sm: "center" }} flexDirection={{ xs: "column", sm: "row" }} gap={1.5}>
                <Typography variant="h5" fontWeight="600" color={colors.grey[100]}>
                  Amount to Pay:
                </Typography>
                <TextField
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                  }}
                  sx={{
                    width: { xs: "100%", sm: 200 },
                    minWidth: { xs: 160, sm: 200 },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: colors.greenAccent[400],
                        borderWidth: 2,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.greenAccent[300],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.greenAccent[400],
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      color: colors.greenAccent[400],
                    },
                  }}
                />
              </Box>

              <Box textAlign="right">
                {needsPayment() ? (
                  <>
                    <Typography variant="h4" fontWeight="700" color={colors.redAccent[400]}>
                      Grand Total: LKR {calculateGrandTotal().toFixed(2)}
                    </Typography>
                    {isAdmin ? (
                      <Button
                        variant="contained"
                        color="success"
                        size={isMobile ? "medium" : "large"}
                        onClick={handleOpenConfirmDialog}
                        disabled={processingPayment || calculateGrandTotal() <= 0}
                        sx={{ minWidth: { xs: "100%", sm: 200 }, mt: 1 }}
                      >
                        {processingPayment ? <CircularProgress size={24} /> : "Record Payment"}
                      </Button>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1, display: "inline-flex" }}>
                        Contact your branch admin to record this payment.
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert severity="success" sx={{ display: 'inline-flex' }}>
                    <Typography variant="body1" fontWeight="600">
                      ✅ Subscription active until {invoice?.latest_expiry_date}
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: colors.primary[400] }}>
        <Typography variant="h4" fontWeight="600" mb={3} sx={{ fontSize: { xs: "1.1rem", sm: "1.4rem", md: "2rem" } }}>
          Payment History (Last 6 Months)
        </Typography>

        <Box
          height={{ xs: "360px", sm: "400px" }}
          sx={{
            overflowX: "auto",
            "& .MuiDataGrid-root": {
              border: "none",
              minWidth: 760,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              fontSize: { xs: "0.76rem", sm: "0.82rem", md: "0.875rem" },
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: { xs: "0.72rem", sm: "0.8rem", md: "0.875rem" },
              fontWeight: 700,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
          }}
        >
          <DataGrid
            rows={paymentHistory}
            columns={columns}
            getRowId={(row) => row.payment_id || Math.random()}
            pageSize={5}
            rowsPerPageOptions={[5]}
          />
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Payment for {effectiveMember.first_name} {effectiveMember.last_name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div" id="confirm-dialog-description">

            {/* Arrears breakdown */}
            {invoice?.unpaid_months?.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight="600" color="error" gutterBottom>
                  Arrears ({invoice.unpaid_months.length} months, incl. {invoice.surcharge_percent}% surcharge):
                </Typography>
                {invoice.unpaid_months.map((month) => (
                  <Box key={`${month.year}-${month.month}`} display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">
                      {month.month_name} (Fee: {month.monthly_fee?.toFixed(2)} + Surcharge: {month.surcharge?.toFixed(2)})
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      LKR {month.total?.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
                <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop="1px solid #ccc">
                  <Typography variant="body2" fontWeight="600">Arrears Subtotal (incl. surcharge):</Typography>
                  <Typography variant="body2" fontWeight="600" color="error">
                    LKR {invoice.arrears_total?.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Current month */}
            <Box mb={2}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Current Month Fee:
              </Typography>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Subscription Fee</Typography>
                <Typography variant="body2" fontWeight="600">
                  LKR {invoice?.monthly_fee?.toFixed(2) || "0.00"}
                </Typography>
              </Box>
              {invoice?.current_month_surcharge > 0 && (
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography variant="body2" color="error">
                    Late Surcharge ({invoice.surcharge_percent}% - after {invoice.surcharge_day}th)
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color="error">
                    LKR {invoice.current_month_surcharge?.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Grand Total */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight="700">GRAND TOTAL:</Typography>
              <Typography variant="h5" fontWeight="700" color="error">
                LKR {calculateGrandTotal().toFixed(2)}
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleRecordPayment} color="success" variant="contained" autoFocus>
            Confirm & Record Payments
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberPayment;
